import { Index, indexBy } from "diagnostics-lib/iterable";
import type { FilterProperties, ImportVersionsEnum } from "./types";
import { filterColumns } from "./types";
import type { ActivationFilter } from "../../../../api/useActivationFilters";
import t from "../../../../i18n";
import type { Quantity } from "../../../../api/useQuantities";
import parseCSV from "../../../../lib/csv";

type FilterCellValue = {
  [key in FilterProperties]: string;
};

interface FilterErrorData {
  line: number | null;
  errors: string[];
  quantity: string | null;
}

export interface InvalidCsvFilters {
  invalidCsv: true;
  errors: FilterErrorData[];
  format?: ImportVersionsEnum;
}

export interface ValidCsvFilters {
  invalidCsv: false;
  data: ActivationFilter[];
  format?: ImportVersionsEnum;
}

export type ParsedCsvFilters = InvalidCsvFilters | ValidCsvFilters;

function mapFilter(
  quantitiesByName: Index<Quantity>,
  filtersByQuantityName: Index<ActivationFilter>,
  filter: FilterCellValue,
) {
  const errors: string[] = [];
  const quantityName = filter.quantity;
  if (filtersByQuantityName.has(quantityName)) {
    errors.push(t("setting.filter.import.error.duplicate_quantity_name", { quantityName }));
  }

  const {
    maximumStd: maximumStdString,
    maximumValue: maximumValueString,
    minimumValue: minimumValueString,
  } = filter;
  if (isValuesNotNull(maximumStdString, maximumValueString, minimumValueString)) {
    errors.push(t("setting.filter.import.error.least_one_filter_value_required", { quantityName }));
    return errors;
  }

  // No further check
  if (quantityName === null) {
    return errors;
  }
  const quantity = quantitiesByName.get(quantityName);

  const maximumStd = maximumStdString === null ? null : Number(maximumStdString);
  const maximumValue = maximumValueString === null ? null : Number(maximumValueString);
  const minimumValue = minimumValueString === null ? null : Number(minimumValueString);
  if (isNumberValues(maximumStd, maximumValue, minimumValue)) {
    errors.push(t("setting.filter.import.error.invalid_number_value", { quantityName }));
    return errors;
  }
  if (isMaxInferiorToMin(maximumValue, minimumValue)) {
    errors.push(t("setting.filter.import.error.max_inferior_to_min", { quantityName }));
  }
  if (isStdPositive(maximumStd)) {
    errors.push(t("setting.filter.import.error.std_positive", { quantityName }));
  }

  if (!quantity) {
    errors.push(
      t("setting.filter.import.error.quantity_not_existing_in_project", { quantityName }),
    );
    return errors;
  }
  if (quantity.type === "FORMULA" && maximumStd !== null) {
    errors.push(
      t("setting.filter.import.error.maximumStd_isnt_allowed_for_formulas", { quantityName }),
    );
  }

  filtersByQuantityName.set(quantityName, {
    quantityId: quantity.id,
    maxStd: maximumStd,
    max: maximumValue,
    min: minimumValue,
  });

  return errors;
}

async function parseCsvFilterValues(file: File, quantities: Quantity[]): Promise<ParsedCsvFilters> {
  const csv = await file.text();
  const filterValues = parseCSV(csv, "line");

  if (filterValues.length === 0) {
    return {
      invalidCsv: true,
      errors: [
        {
          errors: [t("setting.filter.import.error.empty_csv")],
          quantity: null,
          line: null,
        },
      ],
    };
  }
  if (filterValues.length > 0 && filterValues[0]) {
    const filterProperties = Object.keys(filterValues[0]);
    if (filterProperties.length !== filterColumns.length) {
      return {
        invalidCsv: true,
        errors: [
          {
            errors: [t("setting.filter.import.error.columns_expected")],
            quantity: null,
            line: null,
          },
        ],
      };
    }
    for (const columnName of filterProperties) {
      if (!filterColumns.includes(columnName as FilterProperties)) {
        return {
          invalidCsv: true,
          errors: [
            {
              errors: [t("setting.filter.import.error.column_not_expected", { columnName })],
              quantity: null,
              line: null,
            },
          ],
        };
      }
    }
  }

  const quantitiesByName = indexBy(quantities, "name");
  const filtersByQuantityName = new Index<ActivationFilter>();

  const errors: FilterErrorData[] = [];
  for (const [line, filter] of (filterValues as FilterCellValue[]).entries()) {
    const filterErrors = mapFilter(quantitiesByName, filtersByQuantityName, filter);
    if (filterErrors.length > 0) {
      errors.push({
        line: line + 2, // 1 for the header with column names, 1 for the first line
        errors: filterErrors,
        quantity: filter.quantity,
      });
    }
  }

  if (errors.length > 0)
    return {
      invalidCsv: true,
      errors,
    };

  return { invalidCsv: false, data: Array.from(filtersByQuantityName.values()) };
}

export default parseCsvFilterValues;

const isMaxInferiorToMin = (maximumValue: number | null, minimumValue: number | null) =>
  maximumValue !== null && minimumValue !== null && maximumValue < minimumValue;

const isStdPositive = (maximumStd: number | null) => maximumStd !== null && !(maximumStd > 0);

const isNumberValues = (
  maximumStd: number | null,
  maximumValue: number | null,
  minimumValue: number | null,
) => Number.isNaN(maximumStd) || Number.isNaN(maximumValue) || Number.isNaN(minimumValue);

const isValuesNotNull = (
  maximumStdString: string | null,
  maximumValueString: string | null,
  minimumValueString: string | null,
) => maximumStdString === null && maximumValueString === null && minimumValueString === null;
